// @flow
import './index.less';

import {Button} from 'antd';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import React, {useCallback, useMemo} from 'react';
import Markdown from 'react-markdown';

import {useAuthState} from '_contexts/auth';
import {useCheckout, useCheckoutState} from '_contexts/checkout';
import {checkPaymentMethodRequired} from '_utils/checkout';

const CheckoutAction = (): React$Node => {
  const {t} = useTranslation();

  const {currentUser} = useAuthState();
  const {selectedPlan, promotionCode, paymentMethod, purchasing} = useCheckoutState();
  const {purchase} = useCheckout();

  const totalPrice = useMemo(() => {
    if (!selectedPlan) return null;

    const discountPercentage = promotionCode ? promotionCode.coupon.percentOff : 0;
    const origTotalPrice = selectedPlan.unitAmount / 100;

    return discountPercentage > 0 ? (origTotalPrice * (100 - discountPercentage)) / 100 : origTotalPrice;
  }, [selectedPlan, promotionCode]);

  const currency = useMemo(() => selectedPlan?.currency.toUpperCase(), [selectedPlan]);

  const buttonText = useMemo(() => {
    if (totalPrice === null || !currency) {
      return t('Common_BuyNow');
    }
    if (totalPrice === 0) {
      return t('Common_BuyFree');
    }

    return t('Common_BuyNowWithPrice', {
      currency,
      price: totalPrice,
    });
  }, [t, currency, totalPrice]);

  const paymentMethodRequired = useMemo(() => {
    if (!selectedPlan) return true;

    return checkPaymentMethodRequired(selectedPlan, promotionCode);
  }, [selectedPlan, promotionCode]);

  const disabled = useMemo(
    () => !selectedPlan || !currentUser || (paymentMethodRequired && !paymentMethod),
    [selectedPlan, currentUser, paymentMethod, paymentMethodRequired],
  );

  const handlePurchase = useCallback(() => {
    (async () => {
      try {
        await purchase();
      } catch (err) {
        console.log('failed to purchase', err);
      }
    })();
  }, [purchase]);

  return (
    <div className="checkout-action">
      <Button
        className="checkout-btn"
        type="primary"
        block
        disabled={disabled}
        loading={purchasing}
        onClick={handlePurchase}
      >
        {buttonText}
      </Button>
      <div
        className="footnote"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: t(`CheckoutSection_CheckoutDisclamer`),
        }}
      />
    </div>
  );
};

export default CheckoutAction;
